/**
 * Staging & Usage Event Payload Schemas
 *
 * Zod schemas for message staging, usage, and question expiry payloads.
 */

import { z } from 'zod';
import { SurfaceSchema } from './agent-trace.schema.js';

// =============================================================================
// Usage Events
// =============================================================================

export const AgentUsagePayloadSchema = z.object({
  sessionId: z.string().optional(),
  taskId: z.string().optional(),
  model: z.string().optional(),
  usage: z.object({
    input_tokens: z.number(),
    // ONE-CONVENTION marker (2026-08-04): true when input_tokens EXCLUDES the cache buckets;
    // the server's repricer keys its legacy include-convention math off its absence.
    input_tokens_exclusive: z.boolean().optional(),
    output_tokens: z.number(),
    cache_creation_input_tokens: z.number().optional(),
    cache_read_input_tokens: z.number().optional(),
    cache_creation: z
      .object({
        ephemeral_5m_input_tokens: z.number().optional(),
        ephemeral_1h_input_tokens: z.number().optional(),
      })
      .optional(),
    // §4.9: Fugu orchestration usage broken out (attribution only; billing flows via
    // cumulativeCostUsd, NOT these fields). Mapped from the contract usage's discrete
    // orchestration sub-fields by the core sink (DATA-7). Absent for non-Fugu providers.
    orchestration_input_tokens: z.number().optional(),
    orchestration_input_cached_tokens: z.number().optional(),
    orchestration_output_tokens: z.number().optional(),
    // Reasoning-token OBSERVABILITY (never billing), carried on every lane that reports it
    // (the shared usage-to-wire module, h9-C5). Absent when the provider reports none.
    reasoning_tokens: z.number().optional(),
    // FIRST_BYTE_HEDGE_V1: the prompt of a duplicate the first-byte hedge sent and cancelled. Not
    // input (never in input_tokens): the server records it and prices it on its own (hedge-pricing.ts).
    hedged_input_tokens: z.number().optional(),
  }),
  // Per-turn observability (Task 5 / Phase 0) — optional for backward
  // compatibility with older sandbox builds.
  sdk_message_uuid: z.string().optional(),
  turn_number: z.number().optional(),
  sdk_version: z.string().optional(),
  sdk_cost_usd: z.number().optional(),
  // Media-refs S0: the round's serialized request bytes and its wire images (request-meter.ts).
  request_bytes: z.number().optional(),
  image_count: z.number().optional(),
  image_bytes: z.number().optional(),
  // Lane metering: which model call this usage accounts for ('sweep' / 'suggestion', the
  // two epilogue side calls; 'compaction', the summarize call) and whether that call
  // returned. Present only on AGENT_OFFLOOP_USAGE; the MAIN loop's AGENT_USAGE omits both, so
  // its wire shape is byte-identical. An 'error' event carries zero tokens by design: proof
  // the call was attempted, not a gap.
  //
  // 'compaction' is metered like the others but BILLED like the main lane (it is deliberately
  // absent from the server's `isOffloopLane`): its cost already rides the turn's claimed
  // total, so it must also reach the re-priced total or the two legs disagree. 'subagent'
  // (SUBAGENT_USAGE_LEDGER_V1) is billed the same way and for the same reason: an in-process
  // subagent round's cost rides the parent turn's claimed total. See the UsageLane doc in
  // @vestra/agent-sdk contract/events.ts.
  //
  // The memory sweep's status says how it ended: 'ok' at least one fact, 'none' the model said
  // NONE, 'capped' the output cap cut it, 'empty' neither. 'cancelled' (POST_TURN_OFF_PATH_V1):
  // a sent side call cut on purpose because the person's next message arrived first; real
  // spend, never a failure (the usage reported before the cut, usually zero). 'timeout'
  // (SWEEP_PREFIX_PARITY_V1): a sweep riding the loop's request hit its own 30 s bound; real
  // spend, carrying the provider's usage or else the engine's estimate.
  // 'started' (OFFLOOP_CHARGE_HOLD_V1): no outcome. Sent BEFORE an off-loop call's request, under
  // the message id its outcome row will carry, with the engine's estimate of the request; the
  // server opens a charge hold with it and writes no usage row.
  // 'post_turn_compaction' (COMPACTION_POST_TURN_V1): a summarize run after its turn ended, in
  // the idle wait for the next message. Off-loop: charged on its own leg like the sweep.
  lane: z.enum(['main', 'sweep', 'suggestion', 'compaction', 'vision', 'subagent', 'post_turn_compaction']).optional(),
  lane_status: z.enum(['ok', 'error', 'none', 'capped', 'empty', 'cancelled', 'timeout', 'started']).optional(),
  // COMPACTION_POST_TURN_V1: on a compaction lane's event, what it did, for the O1 rail.
  compaction: z
    .object({
      reason: z.enum(['round_head', 'post_turn', 'recovery', 'manual']),
      strategy: z.enum(['local', 'remote']),
      tokens_before: z.number(),
      tokens_after: z.number(),
    })
    .optional(),
});

// =============================================================================
// Context Usage Events
// =============================================================================

export const ContextUsageSnapshotSchema = z.object({
  totalTokens: z.number(),
  maxTokens: z.number(),
  percentage: z.number(),
  model: z.string(),
  // K4F-1: the native arm's per-category split (system prompt, tool schemas, messages,
  // memory), sent beside the flat totals the meter reads.
  system: z.number().optional(),
  tools: z.number().optional(),
  messages: z.number().optional(),
  memory: z.number().optional(),
});

export const AgentContextUsagePayloadSchema = z.object({
  taskId: z.string(),
  sessionId: z.string().optional(),
  usage: ContextUsageSnapshotSchema,
});

// =============================================================================
// Memory Recall Events (SDK 0.3.144)
// =============================================================================

export const MemoryRecallEntrySchema = z.object({
  path: z.string(),
  scope: z.enum(['personal', 'team']),
  content: z.string().optional(),
});

export const AgentMemoryRecallPayloadSchema = z.object({
  taskId: z.string(),
  sessionId: z.string().optional(),
  mode: z.enum(['select', 'synthesize']),
  memories: z.array(MemoryRecallEntrySchema),
});

// =============================================================================
// Message Staging Events
// =============================================================================

/**
 * K6 A0 sub-schemas — kept co-located so the zod enum is the canonical source
 * for the runtime union (the TS type in harness.ts mirrors it structurally but
 * is not derived from zod to avoid a cross-package zod dependency in the pure
 * harness module).
 */
const MessagePrioritySchema = z.enum(['now', 'next', 'later']);

// Two-brain P2 — which SURFACE a send was born from. This copy stays in lockstep
// with the event-payloads.schema.ts copy (deliberately NOT derived — see that
// file's note). Additive-optional: absent = legacy client = treated as 'web' by
// the router.
const TaskSurfaceSchema = z.enum(['web', 'native', 'channel']);

// P4 — MessageOrigin as a runtime discriminated union. This copy stays in lockstep
// with the event-payloads.schema.ts copy and the contract/harness.ts TS mirror
// (deliberately NOT derived — see the module note above). The four legacy kinds parse
// byte-identically; the `initiative` variant carries WHY it fired (`trigger`) + how
// loudly it may surface (`politeness`). A trigger can exist ONLY on an initiative origin.
const InitiativeTriggerSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('scheduled'), scheduleId: z.string(), firedAt: z.string() }),
  z.object({ kind: z.literal('signal'), source: z.string(), eventId: z.string().optional() }),
  z.object({ kind: z.literal('context'), reason: z.string(), score: z.number().optional() }),
]);
const MessageOriginSchema = z.discriminatedUnion('kind', [
  // P5.1 (Agent Teams) — peerName/peerTaskId are additive-optional peer
  // attribution, meaningful only for kind:'peer'. Absent ⇒ byte-identical.
  z.object({
    kind: z.enum(['human', 'channel', 'peer', 'auto-continuation']),
    peerName: z.string().optional(),
    peerTaskId: z.string().optional(),
    // Orchestration Phase 3: helper-to-helper hops on a staged peer message (contract/harness.ts).
    hop: z.number().optional(),
  }),
  z.object({
    kind: z.literal('initiative'),
    trigger: InitiativeTriggerSchema,
    politeness: z.enum(['may-notify', 'silent-unless-actionable', 'draft-only']),
  }),
]);

export const MessageStagedPayloadSchema = z.object({
  taskId: z.string().min(1),
  content: z.string(),
  images: z.array(z.string()).optional(),
  /** K6 A0: client-minted stable id; idempotent join key for cancel/pickup/resync. */
  uuid: z.string().optional(),
  /** K6 A0: queue lane — 'now' | 'next' (default) | 'later'. */
  priority: MessagePrioritySchema.optional(),
  /** K6 A0: provenance of the message. */
  origin: MessageOriginSchema.optional(),
  /** Two-brain P2: surface the send was born from ('web' | 'native' | 'channel'). */
  surface: TaskSurfaceSchema.optional(),
  /** OD2: optional fine-grained trace surface — observability only, never
   *  placement. Rides wherever the coarse `surface` does (single source:
   *  agent-trace.schema.ts SurfaceSchema). */
  traceSurface: SurfaceSchema.optional(),
});

export const MessagePickedUpPayloadSchema = z.object({
  taskId: z.string().min(1),
  /** K6 P5: uuid of the picked-up item (optional — older sandbox builds omit it). */
  uuid: z.string().optional(),
  /** Two-stage interrupt: this pickup is a flush-and-redirect (drop card+stash, no promote). */
  redirect: z.boolean().optional(),
});

export const CancelStagedPayloadSchema = z.object({
  taskId: z.string().min(1),
  /** K6 P5: cancel by client-minted uuid (the idempotent join key). */
  uuid: z.string().optional(),
  /** @deprecated legacy index path — optional for back-compat during migration. */
  index: z.number().int().nonnegative().optional(),
});

export const StagingSyncRequestPayloadSchema = z.object({
  taskId: z.string().min(1),
});

/** K6 P5 (D6): one staged message in a STAGING_SYNC snapshot. */
export const StagedEnvelopeSchema = z.object({
  uuid: z.string().min(1),
  content: z.string(),
  priority: MessagePrioritySchema.optional(),
  origin: MessageOriginSchema.optional(),
});

/**
 * K6 P5 (D6): full staged-queue snapshot (agent → server → frontend).
 * Empty `staged` ⇒ the FE clears all cards (e.g. after an agent-recycle wipes
 * the in-memory queue).
 */
export const StagingSyncPayloadSchema = z.object({
  taskId: z.string().min(1),
  staged: z.array(StagedEnvelopeSchema),
});

export const CheckpointContinuePayloadSchema = z.object({
  taskId: z.string().min(1),
});


